import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PropTypes from "prop-types";

import { ButtonV1 } from "components";
import useToaster from "hooks/useToaster";
import { EDD_TEMPLATE_NAMES, SLA_UPLOAD_CONFIGS } from "constants/eddConfig";
import {
  downloadTemplateFile,
  fetchSlaHistory,
  uploadSlaFile,
} from "services/eddService";

import EddProcessStepper from "../components/EddProcessStepper";
import EddSectionCard from "../components/EddSectionCard";
import RecentChanges from "../components/RecentChanges";
import ExpectedColumnsCard from "./components/ExpectedColumnsCard";
import SampleDataModal from "./components/SampleDataModal";
import { useEddUploadContext } from "../EddUploadContext";

const BADGE_COLOR_MAP = {
  info: { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
  blue: { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
  purple: { bg: "#F3E8FF", color: "#6B21A8", border: "#DDD6FE" },
  teal: { bg: "#ECFDF5", color: "#065F46", border: "#A7F3D0" },
  default: { bg: "#F3F4F6", color: "#374151", border: "#E5E7EB" },
};

const EddCsvUploadTab = ({ tabKey }) => {
  const theme = useTheme();
  const triggerToaster = useToaster();
  const inputRef = useRef(null);

  const { isGlobalUploading, setIsGlobalUploading } = useEddUploadContext();

  const config = SLA_UPLOAD_CONFIGS[tabKey] || {};

  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const isBusy = isUploading || Boolean(isGlobalUploading);
  const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setIsHistoryLoading(true);
    const result = await fetchSlaHistory(tabKey);
    setIsHistoryLoading(false);
    if (result?.data) {
      setHistoryRows(result.data);
    }
  }, [tabKey]);

  useEffect(() => {
    loadHistory();
    setSelectedFile(null);
  }, [loadHistory]);

  const handleFileSelect = (files) => {
    const file = files?.[0];
    if (!file) return;

    const extension = file.name?.split(".").pop()?.toLowerCase();
    if (extension !== "csv") {
      triggerToaster("Please upload a .csv file", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      triggerToaster("File size exceeds 5 MB limit", "error");
      return;
    }

    setSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDownloadTemplate = async () => {
    const templateName = EDD_TEMPLATE_NAMES[tabKey];
    if (!templateName) return;
    const result = await downloadTemplateFile(templateName);
    if (result && !result.success) {
      triggerToaster(result.error || "Failed to download template", "error");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || isBusy) return;

    setIsUploading(true);
    setIsGlobalUploading?.(true);

    try {
      const result = await uploadSlaFile(tabKey, selectedFile);

      if (result.success) {
        triggerToaster(result.message || "File uploaded successfully", "success");
        setSelectedFile(null);
        loadHistory();
      } else {
        triggerToaster(result.error || "Failed to upload file", "error");
      }
    } finally {
      setIsUploading(false);
      setIsGlobalUploading?.(false);
    }
  };

  return (
    <Box>
      {/* 1. Process Flow Stepper Banner */}
      <EddProcessStepper />

      {/* 2. Header Info (Title, Description, Tags, Monospace Code Pill) */}
      <Box mb={4}>
        <Typography variant="h5" fontWeight={700} color="text.primary" mb={1}>
          {config.title}
        </Typography>

        <Typography fontSize={14} color="text.secondary" mb={2} lineHeight={1.5}>
          {config.description}
        </Typography>

        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
          {config.badges?.map((badge) => {
            const bStyle = BADGE_COLOR_MAP[badge.variant] || BADGE_COLOR_MAP.default;
            return (
              <Box
                key={badge.label}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "16px",
                  backgroundColor: bStyle.bg,
                  color: bStyle.color,
                  border: `1px solid ${bStyle.border}`,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {badge.label}
              </Box>
            );
          })}

          {config.tableCode && (
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: "6px",
                backgroundColor: "#111827",
                color: "#F9FAFB",
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.02em",
              }}
            >
              {config.tableCode}
            </Box>
          )}
        </Box>
      </Box>

      {/* 3. Two Columns: Left = Upload File Card (70%), Right = Expected Columns (30%) */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          mb: 6,
          alignItems: "stretch",
        }}
      >
        {/* Left Column: Upload Card (70% width) */}
        <Box
          sx={{
            width: { xs: "100%", md: "70%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              borderRadius: "12px",
              border: `1px solid ${theme.palette.grey[300]}`,
              backgroundColor: "#FFFFFF",
              p: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography fontSize={14} fontWeight={700} letterSpacing="0.05em">
                  UPLOAD FILE
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  CSV · max 5 MB · UTF-8
                </Typography>
              </Box>

              {/* Drag & Drop Area */}
              <Box
                data-testid="sla-dropzone"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !isBusy && inputRef.current?.click()}
                sx={{
                  border: `2px dashed ${
                    isDragging ? theme.palette.primary.main : "#CBD5E1"
                  }`,
                  borderRadius: "12px",
                  p: 4,
                  textAlign: "center",
                  cursor: isBusy ? "not-allowed" : "pointer",
                  backgroundColor: isDragging ? "#F0F9FF" : "#F8FAFC",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: "#F0F9FF",
                  },
                }}
              >
                <input
                  type="file"
                  hidden
                  ref={inputRef}
                  accept=".csv"
                  onClick={(e) => {
                    e.target.value = null;
                  }}
                  onChange={(e) => handleFileSelect(e.target.files)}
                />

                <Typography fontSize={15} fontWeight={600} color="text.primary" mb={1}>
                  Drag CSV here or click to browse
                </Typography>

                <Typography fontSize={12} color="text.secondary" mb={3}>
                  {config.dropzoneSubtext}
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon fontSize="small" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadTemplate();
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    borderColor: "#CBD5E1",
                    color: "text.primary",
                    backgroundColor: "#FFFFFF",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F1F5F9",
                    },
                  }}
                >
                  Download CSV Template
                </Button>
              </Box>

              {/* Selected File Preview Box */}
              {selectedFile && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mt={2}
                  p={2}
                  sx={{
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.grey[300]}`,
                    backgroundColor: "#F8FAFC",
                  }}
                >
                  <Box>
                    <Typography fontSize={13} fontWeight={600} color="text.primary">
                      {selectedFile.name}
                    </Typography>
                    <Typography fontSize={11} color="text.secondary">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    disabled={isBusy}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            {/* Bottom Actions Row */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mt={4}
              pt={2}
            >
              {config.actionLabel ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setIsSampleModalOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#2563EB",
                    p: 0,
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {config.actionLabel}
                </Button>
              ) : (
                <Box />
              )}

              <ButtonV1
                title={isUploading ? "Uploading..." : "Upload"}
                onClick={handleUpload}
                disabled={!selectedFile || isBusy}
                loader={isUploading}
                size="medium"
                sx={{ minWidth: 100 }}
              />
            </Box>
          </Box>
        </Box>

        {/* Right Column: Expected Columns Card (30% width) */}
        <Box
          sx={{
            width: { xs: "100%", md: "30%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ExpectedColumnsCard
            expectedColumns={config.expectedColumns}
            callout={config.callout}
          />
        </Box>
      </Box>

      {/* 4. Bottom Section: Jobs Table */}
      <EddSectionCard title="Recent Changes">
        <RecentChanges rows={historyRows} isLoading={isHistoryLoading} />
      </EddSectionCard>

      {/* 5. Sample Data Modal */}
      <SampleDataModal
        open={isSampleModalOpen}
        onClose={() => setIsSampleModalOpen(false)}
        title={config.actionLabel === "View current active set" ? "Current Active Set" : `${config.title} Sample`}
        headers={config.templateHeaders}
        sampleData={config.sampleData}
        fileName={config.templateFileName?.replace(".csv", "") || `${tabKey.toLowerCase()}_sample`}
      />
    </Box>
  );
};

EddCsvUploadTab.propTypes = {
  tabKey: PropTypes.oneOf([
    "PROCUREMENT_SLA",
    "INVENTORY_LEAD_TIME",
    "SERVICE_CENTRE_SLA",
    "ORDER_PROCESSING_SLA",
  ]).isRequired,
};

export default EddCsvUploadTab;
